/**
 * FloatingToolbar — Advanced Click-to-Select Toolbar
 *
 * Figma/Canva-quality toolbar with contextual actions per element type.
 * Features: formatting, color, opacity, duplicate, copy/paste style,
 * move up/down, quick border-radius, link editing, and element info.
 * Glass-morphism design with smooth position tracking.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  Image, Trash2, Type, Minus, Plus, Palette,
  Box, Link2, Copy, Clipboard, ArrowUp, ArrowDown,
  Layers, Square, Circle, CornerUpLeft, Maximize2,
  FlipHorizontal, RotateCw, Eye, EyeOff, Pipette
} from 'lucide-react';

const TOOLBAR_GAP = 10;

// ── Reusable Button ──────────────────────────────────────────────────
function ToolbarButton({ icon: Icon, title, onClick, isDanger, isActive, badge, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 34, height: 34, border: 'none', borderRadius: 8,
        position: 'relative',
        background: isActive
          ? 'rgba(99,102,241,0.2)'
          : hovered
            ? (isDanger ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.12)')
            : 'transparent',
        color: disabled
          ? 'rgba(255,255,255,0.2)'
          : isActive
            ? '#a5b4fc'
            : hovered
              ? (isDanger ? '#fca5a5' : '#ffffff')
              : '#cbd5e1',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        transition: 'all 0.15s ease',
        transform: hovered && !disabled ? 'scale(1.08)' : 'scale(1)',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <Icon size={15} />
      {badge && (
        <span style={{
          position: 'absolute', top: 2, right: 2,
          width: 6, height: 6, borderRadius: '50%',
          background: '#6366f1',
        }} />
      )}
    </button>
  );
}

// ── Separator ────────────────────────────────────────────────────────
function Separator() {
  return <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.08)', flexShrink: 0, margin: '0 2px' }} />;
}

// ── Mini Color Picker ────────────────────────────────────────────────
function MiniColorButton({ currentColor, onColorChange, title = 'Text Color' }) {
  const inputRef = useRef(null);
  return (
    <div style={{ position: 'relative' }}>
      <button
        title={title}
        onClick={() => inputRef.current?.click()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, border: 'none', borderRadius: 8,
          background: 'transparent', cursor: 'pointer', padding: 0,
          position: 'relative',
        }}
      >
        <Palette size={15} style={{ color: '#cbd5e1' }} />
        <div style={{
          position: 'absolute', bottom: 4, left: 8, right: 8, height: 3,
          borderRadius: 2,
          background: currentColor || '#ffffff',
        }} />
      </button>
      <input
        ref={inputRef}
        type="color"
        value={currentColor || '#ffffff'}
        onChange={(e) => onColorChange(e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </div>
  );
}

// ── Opacity Slider (inline compact) ──────────────────────────────────
function OpacitySlider({ value, onChange }) {
  const [show, setShow] = useState(false);
  const opacity = value ?? 1;
  const pct = Math.round(opacity * 100);

  return (
    <div style={{ position: 'relative' }}>
      <button
        title={`Opacity: ${pct}%`}
        onClick={() => setShow(!show)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, border: 'none', borderRadius: 8,
          background: show ? 'rgba(255,255,255,0.12)' : 'transparent',
          cursor: 'pointer', padding: 0, color: '#cbd5e1',
          transition: 'all 0.15s ease',
        }}
      >
        <Eye size={15} style={{ opacity: opacity }} />
      </button>
      {show && (
        <div
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            marginTop: 8, padding: '8px 12px',
            background: 'rgba(10, 15, 28, 0.95)', backdropFilter: 'blur(20px)',
            borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', gap: 8,
            whiteSpace: 'nowrap', zIndex: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="range"
            min="0" max="1" step="0.05"
            value={opacity}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            style={{
              width: 80, height: 4, cursor: 'pointer',
              accentColor: '#6366f1',
            }}
          />
          <span style={{
            fontSize: 11, fontWeight: 600, color: '#94a3b8',
            fontFamily: 'ui-monospace, monospace', minWidth: 30, textAlign: 'right',
          }}>
            {pct}%
          </span>
        </div>
      )}
    </div>
  );
}

// ── Border Radius Quick Adjuster ─────────────────────────────────────
function RadiusButton({ value, onChange }) {
  const [show, setShow] = useState(false);
  const current = parseInt(value) || 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        title={`Corner roundness: ${current}px`}
        onClick={() => setShow(!show)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, border: 'none', borderRadius: 8,
          background: show ? 'rgba(255,255,255,0.12)' : 'transparent',
          cursor: 'pointer', padding: 0, color: '#cbd5e1',
          transition: 'all 0.15s ease',
        }}
      >
        <Square size={15} style={{ borderRadius: Math.min(current / 4, 4) }} />
      </button>
      {show && (
        <div
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            marginTop: 8, padding: '8px 12px',
            background: 'rgba(10, 15, 28, 0.95)', backdropFilter: 'blur(20px)',
            borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap', zIndex: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {[0, 4, 8, 12, 16, 24, 9999].map((r) => (
            <button
              key={r}
              onClick={() => { onChange(r === 9999 ? '9999px' : `${r}px`); setShow(false); }}
              title={r === 9999 ? 'Full circle' : `${r}px`}
              style={{
                width: 28, height: 28, border: '1px solid',
                borderColor: current === r ? '#6366f1' : 'rgba(255,255,255,0.1)',
                borderRadius: r === 9999 ? '50%' : Math.min(r, 10),
                background: current === r ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#94a3b8', fontFamily: 'monospace',
                transition: 'all 0.15s',
              }}
            >
              {r === 9999 ? <Circle size={10} /> : r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Toolbar ─────────────────────────────────────────────────────
export default function FloatingToolbar({ selectedElement, onAction, containerRef, copiedStyle }) {
  const toolbarRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, visible: false });

  // ── Positioning logic ──
  const reposition = useCallback(() => {
    if (!selectedElement?.domNode || !containerRef?.current || !toolbarRef.current) {
      setPos(p => ({ ...p, visible: false }));
      return;
    }
    const elRect = selectedElement.domNode.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const tbRect = toolbarRef.current.getBoundingClientRect();

    let top = elRect.top - containerRect.top - tbRect.height - TOOLBAR_GAP;
    let left = elRect.left - containerRect.left + (elRect.width - tbRect.width) / 2;

    // Clamp horizontal
    left = Math.max(8, Math.min(left, containerRect.width - tbRect.width - 8));
    // Flip below if no room above
    if (top < 8) {
      top = elRect.bottom - containerRect.top + TOOLBAR_GAP;
    }

    setPos({ top, left, visible: true });
  }, [selectedElement, containerRef]);

  useEffect(() => {
    requestAnimationFrame(reposition);
  }, [reposition, selectedElement]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!selectedElement) return;
    const container = containerRef?.current;
    const handleScroll = () => requestAnimationFrame(reposition);
    if (container) container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [selectedElement, containerRef, reposition]);

  // Click-away to dismiss
  useEffect(() => {
    if (!selectedElement) return;
    const handler = (e) => {
      if (
        toolbarRef.current && !toolbarRef.current.contains(e.target) &&
        selectedElement.domNode && !selectedElement.domNode.contains(e.target)
      ) {
        onAction('dismiss');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedElement, onAction]);

  if (!selectedElement) return null;

  const isText = selectedElement.type === 'text';
  const isImage = selectedElement.type === 'image';
  const isElement = selectedElement.type === 'element';
  const dom = selectedElement.domNode;

  // Get computed values for display
  const computedFontSize = dom ? Math.round(parseFloat(window.getComputedStyle(dom).fontSize) || 16) : 16;
  const computedOpacity = dom ? parseFloat(window.getComputedStyle(dom).opacity) : 1;
  const computedRadius = dom ? window.getComputedStyle(dom).borderRadius : '0px';
  const computedColor = dom ? window.getComputedStyle(dom).color : '';
  const computedBgColor = dom ? window.getComputedStyle(dom).backgroundColor : '';

  // Element size info
  const elWidth = dom ? Math.round(dom.offsetWidth) : 0;
  const elHeight = dom ? Math.round(dom.offsetHeight) : 0;

  return (
    <div
      ref={toolbarRef}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        opacity: pos.visible ? 1 : 0,
        pointerEvents: pos.visible ? 'auto' : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '5px 8px',
        background: 'rgba(10, 15, 28, 0.95)',
        backdropFilter: 'blur(20px) saturate(1.5)',
        borderRadius: 14,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)',
        zIndex: 9999,
        transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease, transform 0.15s ease',
        transform: pos.visible ? 'translateY(0)' : 'translateY(6px)',
        userSelect: 'none',
        flexWrap: 'wrap',
        maxWidth: 'calc(100vw - 40px)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Type indicator + dimensions ── */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '0 6px',
        color: isText ? '#93c5fd' : isImage ? '#a78bfa' : '#86efac',
        fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
        gap: '4px',
      }}>
        {isText ? <Type size={12} /> : isImage ? <Image size={12} /> : <Box size={12} />}
        <span>{isText ? 'Text' : isImage ? 'Image' : 'Element'}</span>
        <span style={{
          fontSize: 9, color: 'rgba(255,255,255,0.25)', fontWeight: 400,
          fontFamily: 'ui-monospace, monospace', marginLeft: 2,
        }}>
          {elWidth}×{elHeight}
        </span>
      </div>
      <Separator />

      {/* ── Text formatting ── */}
      {isText && (
        <>
          <ToolbarButton icon={Bold} title="Bold (Ctrl+B)" onClick={() => onAction('bold')} />
          <ToolbarButton icon={Italic} title="Italic (Ctrl+I)" onClick={() => onAction('italic')} />
          <ToolbarButton icon={Underline} title="Underline (Ctrl+U)" onClick={() => onAction('underline')} />
          <ToolbarButton icon={Strikethrough} title="Strikethrough" onClick={() => onAction('strikethrough')} />
          <Separator />
          <ToolbarButton icon={AlignLeft} title="Align Left" onClick={() => onAction('align-left')} />
          <ToolbarButton icon={AlignCenter} title="Align Center" onClick={() => onAction('align-center')} />
          <ToolbarButton icon={AlignRight} title="Align Right" onClick={() => onAction('align-right')} />
          <Separator />
          {/* Font size controls */}
          <ToolbarButton icon={Minus} title="Decrease font size" onClick={() => onAction('font-size-decrease')} />
          <span style={{
            fontSize: '11px', color: '#94a3b8', fontWeight: 500,
            minWidth: '24px', textAlign: 'center', userSelect: 'none',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}>
            {computedFontSize}
          </span>
          <ToolbarButton icon={Plus} title="Increase font size" onClick={() => onAction('font-size-increase')} />
          <Separator />
          <MiniColorButton
            currentColor={dom?.style?.color || computedColor || ''}
            onColorChange={(color) => onAction('text-color', color)}
            title="Text Color"
          />
          <Separator />
          <ToolbarButton icon={Link2} title="Add/Edit Link" onClick={() => onAction('link')} />
          <Separator />
        </>
      )}

      {/* ── Image actions ── */}
      {isImage && (
        <>
          <ToolbarButton icon={Image} title="Replace Image" onClick={() => onAction('replace-image')} />
          <RadiusButton
            value={computedRadius}
            onChange={(val) => onAction('border-radius', val)}
          />
          <Separator />
        </>
      )}

      {/* ── Element actions (containers) ── */}
      {isElement && (
        <>
          <MiniColorButton
            currentColor={computedBgColor}
            onColorChange={(color) => onAction('background-color', color)}
            title="Background Color"
          />
          <RadiusButton
            value={computedRadius}
            onChange={(val) => onAction('border-radius', val)}
          />
          <Separator />
        </>
      )}

      {/* ── Universal actions ── */}
      <OpacitySlider
        value={computedOpacity}
        onChange={(val) => onAction('opacity', val)}
      />
      <Separator />

      {/* Copy / Paste style */}
      <ToolbarButton
        icon={Copy}
        title="Copy this element's look"
        onClick={() => onAction('copy-style')}
      />
      <ToolbarButton
        icon={Clipboard}
        title="Apply copied look here"
        onClick={() => onAction('paste-style')}
        badge={!!copiedStyle}
        disabled={!copiedStyle}
      />
      <Separator />

      {/* Duplicate */}
      <ToolbarButton icon={Layers} title="Make a copy (Ctrl+D)" onClick={() => onAction('duplicate')} />

      {/* Move up/down */}
      <ToolbarButton icon={ArrowUp} title="Move Up" onClick={() => onAction('move-up')} />
      <ToolbarButton icon={ArrowDown} title="Move Down" onClick={() => onAction('move-down')} />
      <Separator />

      {/* Delete */}
      <ToolbarButton icon={Trash2} title="Delete (Del)" onClick={() => onAction('delete')} isDanger />
    </div>
  );
}
